import React, { useState, useEffect } from "react";
import { FaSearch, FaTimes } from "react-icons/fa";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";
import { useNavigate } from "react-router-dom";
import { getServices } from "../api/admin/FrServicesApi";
import { useNotify } from "../context/NotifyContext";

const RightPanel = () => {
  const [services, setServices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { notify } = useNotify();

  const fetchServices = async () => {
    try {
      const res = await getServices();
      if (!res?.success) {
        notify(res?.message || "Failed to fetch services", "error");
        return;
      }
      setServices(res?.data || []);
    } catch (error) {
      notify("Something went wrong while fetching services", "error");
      console.error("fetchServices error:", error);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const filteredServices = (services || []).filter((s) =>
    s?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      {/* Mobile-only FAB — icon only, no "Services" label */}
      <button
        type="button"
        className="lg:hidden fixed bottom-4 right-4 z-50 w-11 h-11 rounded-[3px]
          flex items-center justify-center bg-[var(--primary-color)] text-white shadow-ds"
        onClick={() => setIsOpen(true)}
        aria-label="Open services"
      >
        <AppsOutlinedIcon sx={{ fontSize: 20 }} />
      </button>

      <div
        className={`ds-overlay lg:hidden ${
          isOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
        }`}
        onClick={() => setIsOpen(false)}
        aria-hidden={!isOpen}
      />

      <aside
        className={`fixed top-0 right-0 h-full w-72 z-0 p-3 transform transition-transform duration-300
          bg-[var(--surface-color)] border-l border-[var(--border-color)]
          ${isOpen ? "translate-x-0" : "translate-x-full"}
          lg:translate-x-0 lg:static lg:flex lg:flex-col lg:w-full lg:h-full lg:border-0`}
      >
        <div className="flex justify-end lg:hidden mb-3">
          <button
            type="button"
            className="ds-btn ds-btn-ghost !p-2"
            onClick={() => setIsOpen(false)}
            aria-label="Close"
          >
            <FaTimes size={14} />
          </button>
        </div>

        <div className="relative mb-3 shrink-0">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] text-[12px] pointer-events-none" />
          <input
            type="search"
            placeholder="Search here..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="ds-input !w-full !pl-9 !h-9 !rounded-[3px] !text-[12px]"
          />
        </div>

        <div className="space-y-2 overflow-y-auto flex-1 min-h-0 pr-0.5 pb-2 ds-scroll">
          {filteredServices.length > 0 ? (
            filteredServices.map((s, i) => (
              <div
                key={s?._id || s?.slug || i}
                className={`ds-card !p-2.5 flex flex-col gap-2 ${
                  !s?.isActive ? "opacity-55 pointer-events-none" : ""
                }`}
              >
                <div className="flex items-center">
                  <div
                    className="w-8 h-8 rounded-[3px] flex items-center justify-center border border-dashed"
                    style={{
                      borderColor: "var(--error-color)",
                      background:
                        "color-mix(in srgb, var(--error-color) 8%, transparent)",
                    }}
                  >
                    <MonetizationOnOutlinedIcon
                      sx={{ color: "var(--error-color)", fontSize: 20 }}
                    />
                  </div>
                </div>

                <div className="flex justify-between items-center gap-2">
                  <span className="text-[11px] font-semibold text-[var(--text-dark)] leading-tight line-clamp-2">
                    {s?.name || "Unnamed Service"}
                  </span>
                  <button
                    type="button"
                    className="shrink-0 w-7 h-7 rounded-[3px] flex items-center justify-center bg-[var(--primary-color)] text-white hover:brightness-95 transition"
                    onClick={() => {
                      if (s?.isActive) {
                        navigate(`/service/${s?.slug}`);
                      } else {
                        notify("This service is inactive", "info");
                      }
                    }}
                    aria-label={`Open ${s?.name || "service"}`}
                  >
                    <ArrowForwardIosOutlinedIcon sx={{ fontSize: 11, color: "#fff" }} />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <p className="text-[var(--text-muted)] text-[12px] text-center py-6">
              No services found.
            </p>
          )}
        </div>
      </aside>
    </>
  );
};

export default RightPanel;
